import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { works } from "@/mocks/works";
import { newsList } from "@/mocks/news";

const NAV_ITEMS = [
  { to: "/", label: "首页" },
  { to: "/works", label: "作品" },
  { to: "/news", label: "资讯" },
  { to: "/about", label: "关于" },
];

interface Suggestion {
  key: string;
  label: string;
  sub: string;
  to: string;
}

export default function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);
  const boxRef = useRef<HTMLDivElement | null>(null);

  const isHome = location.pathname === "/";
  const transparent = isHome && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setFocused(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(event.target as Node)) {
        setFocused(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const keyword = query.trim();
  const suggestions: Suggestion[] = keyword
    ? [
        ...works
          .filter(
            (w) =>
              w.title.includes(keyword) ||
              w.originalTitle.includes(keyword) ||
              w.genres.some((g) => g.includes(keyword)),
          )
          .slice(0, 5)
          .map((w) => ({
            key: `w-${w.id}`,
            label: w.title,
            sub: `${w.year} · ${w.type === "movie" ? "电影" : "电视剧"}`,
            to: `/works/detail/${w.id}`,
          })),
        ...newsList
          .filter((n) => n.title.includes(keyword) || n.summary.includes(keyword))
          .slice(0, 3)
          .map((n) => ({
            key: `n-${n.id}`,
            label: n.title,
            sub: `资讯 · ${n.date}`,
            to: `/news/detail/${n.id}`,
          })),
      ]
    : [];

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    if (!keyword) return;
    setFocused(false);
    setMenuOpen(false);
    navigate(`/works?q=${encodeURIComponent(keyword)}`);
  };

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    [
      "text-sm md:text-[15px] tracking-wide transition-colors whitespace-nowrap cursor-pointer",
      transparent
        ? isActive
          ? "text-accent-300 font-semibold"
          : "text-background-50/85 hover:text-background-50"
        : isActive
          ? "text-primary-600 font-semibold"
          : "text-foreground-700 hover:text-primary-600",
    ].join(" ");

  return (
    <header
      className={[
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
        transparent
          ? "bg-transparent"
          : "bg-background-50/90 backdrop-blur border-b border-background-200",
      ].join(" ")}
    >
      <div className="px-4 md:px-8 h-16 md:h-20 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3 shrink-0 cursor-pointer">
          <span className="w-10 h-10 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center font-heading text-lg font-semibold">
            叶
          </span>
          <span className="flex flex-col leading-tight">
            <span
              className={[
                "font-heading text-base md:text-lg font-semibold tracking-wide",
                transparent ? "text-background-50" : "text-foreground-950",
              ].join(" ")}
            >
              叶山小百合
            </span>
            <span
              className={[
                "text-[10px] md:text-xs tracking-wider",
                transparent ? "text-background-50/70" : "text-foreground-500",
              ].join(" ")}
            >
              HAYAMA SAYURI ARCHIVE
            </span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {NAV_ITEMS.map((item) => (
            <NavLink key={item.to} to={item.to} className={navLinkClass} end={item.to === "/"}>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:block relative" ref={boxRef}>
          <form onSubmit={submitSearch} className="relative">
            <span
              className={[
                "absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center",
                transparent ? "text-background-50/70" : "text-foreground-400",
              ].join(" ")}
            >
              <i className="ri-search-line text-sm"></i>
            </span>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => setFocused(true)}
              placeholder="搜索作品或资讯"
              className={[
                "w-56 lg:w-64 h-10 pl-9 pr-3 rounded-full text-sm border transition-colors outline-none",
                transparent
                  ? "bg-background-50/15 border-background-50/30 text-background-50 placeholder:text-background-50/60 focus:border-accent-300"
                  : "bg-background-100 border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400",
              ].join(" ")}
            />
          </form>
          {focused && keyword.length > 0 && (
            <div className="absolute right-0 mt-2 w-80 bg-background-50 rounded-lg border border-background-200 overflow-hidden animate-fade-in">
              {suggestions.length > 0 ? (
                <ul className="max-h-80 overflow-y-auto py-1">
                  {suggestions.map((item) => (
                    <li key={item.key}>
                      <Link
                        to={item.to}
                        className="flex items-start gap-3 px-4 py-2.5 hover:bg-background-100 cursor-pointer"
                      >
                        <span className="w-5 h-5 mt-0.5 flex items-center justify-center text-foreground-400 shrink-0">
                          <i className="ri-file-list-3-line text-sm"></i>
                        </span>
                        <span className="min-w-0">
                          <span className="block text-sm text-foreground-900 truncate">
                            {item.label}
                          </span>
                          <span className="block text-xs text-foreground-500">{item.sub}</span>
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="px-4 py-4 text-sm text-foreground-500">未找到相关作品或资讯</div>
              )}
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={() => setMenuOpen((prev) => !prev)}
          aria-label="打开菜单"
          className={[
            "md:hidden w-10 h-10 flex items-center justify-center rounded-md transition-colors cursor-pointer",
            transparent ? "text-background-50" : "text-foreground-900",
          ].join(" ")}
        >
          <i className={menuOpen ? "ri-close-line text-2xl" : "ri-menu-line text-2xl"}></i>
        </button>
      </div>

      {menuOpen && (
        <div className="md:hidden bg-background-50 border-b border-background-200 animate-fade-in">
          <div className="px-4 pb-5 pt-2">
            <form onSubmit={submitSearch} className="relative mb-3">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-foreground-400">
                <i className="ri-search-line text-sm"></i>
              </span>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="搜索作品或资讯"
                className="w-full h-11 pl-9 pr-3 rounded-full text-sm bg-background-100 border border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 outline-none"
              />
            </form>
            <nav className="flex flex-col">
              {NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  className={({ isActive }) =>
                    [
                      "px-2 py-3 text-[15px] border-b border-background-100 cursor-pointer",
                      isActive ? "text-primary-600 font-semibold" : "text-foreground-800",
                    ].join(" ")
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
          </div>
        </div>
      )}
    </header>
  );
}